<template>
  <div>
    <div
      v-if="showOutbound"
      class="search-result__outbound"
    >
      <JourneyList
        :travels="outboundTravels"
        :services="outboundServices"
        :show-outbound="showOutbound"
        :show-inbound-travels="handleShowInboundTravels"
        :is-rebooking="isRebooking"
        :items-to-replace="itemsToReplace"
        @bundle-selected="updateSelectedTravel"
        @update-show-outbound="handleShowOutbound"
      />
    </div>
    <div
      v-if="
        !showOutbound &&
        returnTravels.length &&
        selectedTravel &&
        !isInboundOnlyRebooking
      "
      class="search-result__return"
    >
      <JourneyList
        :travels="returnTravels"
        :services="returnServices"
        :show-outbound="showOutbound"
        :show-inbound-travels="handleShowInboundTravels"
        :is-rebooking="isRebooking"
        :items-to-replace="itemsToReplace"
        @bundle-selected="updateSelectedReturnTravel"
        @update-show-outbound="handleShowOutbound"
      />
    </div>
    <div
      v-if="isInboundOnlyRebooking"
      class="search-result__inbound-only"
    >
      <InboundJourneyList
        :travels="returnTravels"
        :services="returnServices"
        :is-rebooking="isRebooking"
        :items-to-replace="itemsToReplace"
        @bundle-selected="updateSelectedReturnTravel"
      />
    </div>
    <div
      v-if="errorMessage"
      class="error-message"
    >
      {{ $t(`searchResult.error.${errorMessage}`) }}
    </div>
  </div>
</template>

<script lang="ts">
  import {
    defineComponent,
    computed,
    ref,
    watch,
    onMounted,
    PropType,
  } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { Travel } from '@/types/travelTypes'
  import type { SearchSelection, SearchService } from '@/types/searchTypes'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { usePriceStore } from '@/stores/priceStore'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { useRoute } from 'vue-router'
  import JourneyList from '@/components/TravelPlanner/JourneyList/JourneyList.vue'
  import InboundJourneyList from '@/components/TravelPlanner/JourneyList/InboundJourneyList.vue'

  export default defineComponent({
    name: 'JourneySearchResult',
    components: {
      JourneyList,
      InboundJourneyList,
    },
    props: {
      showOutbound: {
        type: Boolean,
        required: true,
      },
      itemsToReplace: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      isRebooking: {
        type: Boolean,
        default: false,
      },
    },
    emits: [
      'update-selected-travel',
      'update-selected-return-travel',
      'update-show-outbound',
    ],
    setup(props, { emit }) {
      const { t } = useI18n()
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const priceStore = usePriceStore()
      const seatReservationStore = useSeatReservationStore()
      const myTravelStore = useMyTravelStore()
      const route = useRoute()
      const errorMessage = ref<string | undefined>(undefined)
      const selectedTravel = ref<SearchSelection | null>(null)
      const selectedReturnTravel = ref<SearchSelection | null>(null)
      const outboundServices = ref<SearchService[]>([])
      const returnServices = ref<SearchService[]>([])

      const isRebooking = computed(
        () => props.isRebooking || route.query.isRebooking === 'true'
      )
      const isEntireJourney = computed(
        () => route.query.entireJourney === 'true'
      )

      const isInboundOnlyRebooking = computed(() => {
        return (
          isRebooking.value &&
          route.query.rebookingType === 'inbound' &&
          route.query.entireJourney !== 'true'
        )
      })

      const outboundTravels = computed(() => {
        return (journeyStore.journeySearchResults?.data?.offer?.travels?.filter(
          (travel) => travel.direction === 'outbound'
        ) as Travel[]) || []
      })

      const returnTravels = computed(() => {
        return (journeyStore.journeySearchResults?.data?.offer?.travels?.filter(
          (travel) => travel.direction === 'inbound'
        ) as Travel[]) || []
      })

      watch(
        () => journeyStore.journeySearchResults?.data?.offer?.travels,
        (newTravels) => {
          selectedTravel.value = null
          selectedReturnTravel.value = null
          priceStore.clear()
          bookingStore.clearTravels()
          // seatReservationStore.clearSelectedSeats()
          myTravelStore.setOutboundTravel(null)
          myTravelStore.setInboundTravel(null)

          if (newTravels) {
            const services: SearchService[] = []

            newTravels.forEach((travel) => {
              travel.routes?.forEach((route) => {
                if (route.legs && route.legs.length > 0) {
                  const leg = route.legs[0] as any
                  const service: SearchService = {
                    id: leg.service_identifier || '',
                    name: leg.service_name || '',
                    schedule: leg.service_schedule_date || '',
                    service_identifier: leg.service_identifier,
                    meta_data: leg.meta_data || [],
                    via_stations: leg.via_stations || []
                  }
                  services.push(service)
                }
              })
              })

            const outboundServicesData = services.filter((_, index) => {
              return index < services.length / 2
            })

            const returnServicesData = services.filter((_, index) => {
              return index >= services.length / 2
            })

            outboundServices.value = outboundServicesData
            returnServices.value = returnServicesData.length
              ? returnServicesData
              : outboundServicesData
          }

          if (!isInboundOnlyRebooking.value) {
            emit('update-show-outbound', true)
          }
        },
        { deep: true }
      )


      const updateSelectedTravel = ({
        travelIndex,
        routeIndex,
        bundleIndex,
        tariffCode,
      }: {
        travelIndex: number
        routeIndex: number
        bundleIndex: number
        tariffCode: string
      }) => {
        const travels =
          journeyStore.journeySearchResults?.data?.offer?.travels || []
        const selectedTravelData = travels.find(
          (travel) => travel.direction === 'outbound'
        )

        if (selectedTravelData?.routes?.[routeIndex]) {
          selectedTravel.value = {
            travelIndex,
            routeIndex,
            bundleIndex,
            tariffCode,
          }

          const travelWithIndex = {
            ...selectedTravelData,
            travel_index: 0,
          }

          bookingStore.setOutboundTravel(
            travelWithIndex as unknown as Travel,
            routeIndex,
            bundleIndex
          )

          const selectedBundle =
            selectedTravelData.routes[routeIndex].bundles?.[bundleIndex]
          if (selectedBundle) {
            priceStore.clear()
            const passengers = isRebooking.value
              ? bookingStore.bookingData?.passengers || []
              : journeyStore.journeySearchResults?.data?.offer?.passengers || []

            passengers.forEach((passenger: any) => {
              if ('id' in passenger && 'type' in passenger) {
                priceStore.addTicket({
                  passenger_id: passenger.id,
                  passenger_type: isRebooking.value
                    ? passenger.type
                    : passenger.tariff_code,
                  product_family: tariffCode,
                  price: selectedBundle.price,
                  originalPrice: selectedBundle.original_price,
                  travelIndex: 0,
                  routeIndex,
                })
              }
            })
          }

          emit('update-selected-travel', selectedTravel.value)
        }
      }

      const updateSelectedReturnTravel = ({
        travelIndex,
        routeIndex,
        bundleIndex,
        tariffCode,
      }: {
        travelIndex: number
        routeIndex: number
        bundleIndex: number
        tariffCode: string
      }) => {
          const travels =
          journeyStore.journeySearchResults?.data?.offer?.travels || []
        const selectedTravelData = travels.find(
          (travel) => travel.direction === 'inbound'
        )

        if (selectedTravelData?.routes?.[routeIndex]) {
          selectedReturnTravel.value = {
            travelIndex,
            routeIndex,
            bundleIndex,
            tariffCode,
          }

          const travelWithIndex = {
            ...selectedTravelData,
            travel_index: 1,
          }

          bookingStore.setInboundTravel(
            travelWithIndex as unknown as Travel,
            routeIndex,
            bundleIndex
          )
          
          const myTravelStore = useMyTravelStore()
          myTravelStore.setHasReturnTravel(true)

          // Update price in price store
          const selectedBundle =
            selectedTravelData.routes[routeIndex].bundles?.[bundleIndex]
          if (selectedBundle) {
            const passengers = isRebooking.value
              ? bookingStore.bookingData?.passengers || []
              : journeyStore.journeySearchResults?.data?.offer?.passengers || []

            passengers.forEach((passenger: any) => {
              if ('id' in passenger && 'type' in passenger) {
                priceStore.addTicket({
                  passenger_id: passenger.id,
                  passenger_type: isRebooking.value
                    ? passenger.type
                    : passenger.tariff_code,
                  product_family: tariffCode,
                  price: selectedBundle.price,
                  originalPrice: selectedBundle.original_price,
                  travelIndex: 1,
                  routeIndex,
                })
              }
            })
          }

          emit('update-selected-return-travel', selectedReturnTravel.value)
        }
      }

      const handleShowInboundTravels = () => {
        emit('update-show-outbound', false)
      }

      const handleShowOutbound = (value: boolean) => {
        emit('update-show-outbound', value)
      }

      watch(
        () => selectedTravel.value,
        (newValue) => {
          if (newValue && isRebooking.value && isEntireJourney.value) {
            emit('update-selected-travel', newValue)
          }
        }
      )

      watch(
        () => selectedReturnTravel.value,
        (newValue) => {
          if (newValue) {
            emit('update-selected-return-travel', newValue)
          }
        }
      )

      watch(
        () => returnTravels.value,
        (newReturnTravels) => {

          if (newReturnTravels?.length && returnServices.value.length === 0) {
            const services: SearchService[] = []

            try {
              newReturnTravels.forEach((travel) => {
                travel.routes?.forEach((route) => {
                  if (route.legs && route.legs.length > 0) {
                    const service = {
                      service_identifier: route.legs[0].service_identifier,
                      meta_data: route.legs[0].meta_data || [],
                      via_stations: route.legs[0].via_stations || [],
                    } as SearchService

                    services.push(service)
                  }
                })
              })

              if (services.length === 0 && isInboundOnlyRebooking.value) {
                newReturnTravels.forEach((travel) => {
                  travel.routes?.forEach((route) => {
                    const dummyService: SearchService = {
                      id: route.id || '',
                      name: route.id?.split('|')[2] || '',
                      schedule: '',
                      service_identifier: route.id,
                      meta_data: [],
                      via_stations: []
                    }
                    services.push(dummyService)
                  })
                })
              }

              returnServices.value = services
            } catch (error) {
              console.error('Error creating services:', error)
            }
          }
        },
        { immediate: true }
      )

      onMounted(() => {
        journeyStore.selectedProducts = []
        priceStore.clear()
      })

      return {
        outboundTravels,
        returnTravels,
        outboundServices,
        returnServices,
        selectedTravel,
        selectedReturnTravel,
        errorMessage,
        isInboundOnlyRebooking,
        updateSelectedTravel,
        updateSelectedReturnTravel,
        handleShowInboundTravels,
        handleShowOutbound,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .error-message {
    color: red;
    margin-top: 1rem;
  }
  .search-result__no-results {
    text-align: center;
    padding: 2rem;
    font-size: 1rem;
  }
</style>
